import { motion } from 'framer-motion';
import clsx from 'clsx';
import type { ButtonHTMLAttributes, PropsWithChildren, ReactNode } from 'react';
import { haptic } from '@/shared/telegram';

/** Экран с безопасными отступами Telegram и общей анимацией появления. */
export function Screen({
  children,
  className,
}: PropsWithChildren<{ className?: string }>): ReactNode {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.22, ease: 'easeOut' }}
      className={clsx('flex h-full w-full flex-col', className)}
      style={{ paddingTop: 'var(--safe-top)', paddingBottom: 'var(--safe-bottom)' }}
    >
      {children}
    </motion.div>
  );
}

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-accent text-white active:bg-accent-strong',
  secondary: 'bg-surface-raised text-text active:bg-border',
  danger: 'bg-danger/15 text-danger active:bg-danger/25',
  ghost: 'bg-transparent text-text-muted active:text-text',
};

export function Button({
  variant = 'primary',
  className,
  children,
  onClick,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant }): ReactNode {
  return (
    <button
      type="button"
      className={clsx(
        'flex min-h-[52px] w-full items-center justify-center gap-2 rounded-2xl px-5 text-[16px] font-medium',
        'transition-[transform,background-color] duration-150 active:scale-[0.985]',
        'disabled:pointer-events-none disabled:opacity-40',
        VARIANTS[variant],
        className,
      )}
      onClick={(event) => {
        haptic.impact('light');
        onClick?.(event);
      }}
      {...rest}
    >
      {children}
    </button>
  );
}


/** Скелет загрузки: заполняет паузу, пока идёт вход через Telegram. */
export function Skeleton({ className }: { className?: string }): ReactNode {
  return (
    <div
      className={clsx('animate-pulse rounded-xl bg-surface-raised/70', className)}
      aria-hidden
    />
  );
}

export function Modal({
  open,
  title,
  children,
  onClose,
}: PropsWithChildren<{ open: boolean; title: string; onClose: () => void }>): ReactNode {
  if (!open) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-[2px]"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 40 }}
        animate={{ y: 0 }}
        exit={{ y: 40 }}
        transition={{ type: 'spring', stiffness: 380, damping: 32 }}
        className="w-full max-w-md rounded-t-3xl bg-surface p-5"
        style={{ paddingBottom: 'calc(1.25rem + var(--safe-bottom))' }}
        onClick={(event) => event.stopPropagation()}
      >
        <h3 className="mb-4 text-center text-[17px] font-semibold text-text">{title}</h3>
        {children}
      </motion.div>
    </motion.div>
  );
}
